<template>
    <div class="stat-item formula-item" :class="themeClass">
        <div class="efficiency-formula">
            <!-- Attack Formula -->
            <template v-if="category === 'attack'">
                <div class="formula-fraction">
                    <div class="formula-numerator">
                        <span class="formula-box formula-kills">
                            <span class="formula-box-label">Очки</span>
                            <span class="formula-box-value">
                                {{ player.attack_kills }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.attack_kills, player.attack_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-blocked">
                            <span class="formula-box-label">Блок</span>
                            <span class="formula-box-value">
                                {{ player.attack_blocked }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.attack_blocked, player.attack_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-errors">
                            <span class="formula-box-label">Ошибки</span>
                            <span class="formula-box-value">
                                {{ player.attack_errors }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.attack_errors, player.attack_total) }}%)
                                </span>
                            </span>
                        </span>
                    </div>
                    <div class="formula-fraction-line"></div>
                    <div class="formula-denominator">
                        <span class="formula-box formula-total">
                            <span class="formula-box-label">Всего атак</span>
                            <span class="formula-box-value">{{ player.attack_total }}</span>
                        </span>
                    </div>
                </div>
                <span class="formula-text"> = </span>
                <span class="formula-box formula-result">
                    <span class="formula-box-label">Эффективность</span>
                    <span class="formula-box-value">{{ formatEfficiency(player.attack_efficiency) }}%</span>
                </span>
            </template>

            <!-- Serve Formula -->
            <template v-else-if="category === 'serve'">
                <div class="formula-fraction">
                    <div class="formula-numerator">
                        <span class="formula-box formula-aces">
                            <span class="formula-box-label">Очки</span>
                            <span class="formula-box-value">
                                {{ player.serve_aces }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.serve_aces, player.serve_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> + </span>
                        <span class="formula-box formula-very-positive"
                            title="Мяч после приема вернулся на свою сторону">
                            <span class="formula-box-label">/</span>
                            <span class="formula-box-value">
                                {{ player.serve_very_positive || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.serve_very_positive || 0, player.serve_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> + </span>
                        <span class="formula-box formula-positive" title="Позитивная подача">
                            <span class="formula-box-label">-</span>
                            <span class="formula-box-value">
                                {{ player.serve_positive || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.serve_positive || 0, player.serve_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> + </span>
                        <span class="formula-box formula-insufficient" title="Недостаточная подача">
                            <span class="formula-box-label">!</span>
                            <span class="formula-box-value">
                                {{ player.serve_insufficient || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.serve_insufficient || 0, player.serve_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-errors">
                            <span class="formula-box-label">Ошибки</span>
                            <span class="formula-box-value">
                                {{ player.serve_errors }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.serve_errors, player.serve_total) }}%)
                                </span>
                            </span>
                        </span>
                    </div>
                    <div class="formula-fraction-line"></div>
                    <div class="formula-denominator">
                        <span class="formula-box formula-total">
                            <span class="formula-box-label">Всего подач</span>
                            <span class="formula-box-value">{{ player.serve_total }}</span>
                        </span>
                    </div>
                </div>
                <span class="formula-text"> = </span>
                <span class="formula-box formula-result">
                    <span class="formula-box-label">Эффективность</span>
                    <span class="formula-box-value">{{ formatEfficiency(player.serve_efficiency) }}%</span>
                </span>
            </template>

            <!-- Block Formula -->
            <template v-else-if="category === 'block'">
                <div class="formula-fraction">
                    <div class="formula-numerator">
                        <span class="formula-box formula-kills">
                            <span class="formula-box-label">Очки</span>
                            <span class="formula-box-value">
                                {{ player.block_kills || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.block_kills || 0, player.block_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> + </span>
                        <span class="formula-box formula-soft">
                            <span class="formula-box-label">Смягчение</span>
                            <span class="formula-box-value">
                                {{ player.block_soft || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.block_soft || 0, player.block_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-remain"
                            title="Нейтральный блок / &quot;касание блока&quot; без явного выигрыша розыгрыша и без ошибки, но с переходом мяча на сторону соперника. Это хуже по качеству, чем +, потому что смягчение (+) предполагает контролируемое доигрывание, а / — просто факт касания без преимущества">
                            <span class="formula-box-label">/</span>
                            <span class="formula-box-value">
                                {{ player.block_remain || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.block_remain || 0, player.block_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-errors">
                            <span class="formula-box-label">ошибки</span>
                            <span class="formula-box-value">
                                {{ player.block_errors || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.block_errors || 0, player.block_total) }}%)
                                </span>
                            </span>
                        </span>
                    </div>
                    <div class="formula-fraction-line"></div>
                    <div class="formula-denominator">
                        <span class="formula-box formula-total">
                            <span class="formula-box-label">Всего блоков</span>
                            <span class="formula-box-value">{{ player.block_total || 0 }}</span>
                        </span>
                    </div>
                </div>
                <span class="formula-text"> = </span>
                <span class="formula-box formula-result">
                    <span class="formula-box-label">Эффективность</span>
                    <span class="formula-box-value">{{ formatEfficiency(player.block_efficiency) }}%</span>
                </span>
            </template>

            <!-- Reception Formula -->
            <template v-else-if="category === 'reception'">
                <div class="formula-fraction">
                    <div class="formula-numerator">
                        <span class="formula-box formula-kills">
                            <span class="formula-box-label">Отличный</span>
                            <span class="formula-box-value">
                                {{ player.reception_perfect || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.reception_perfect || 0, player.reception_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> + </span>
                        <span class="formula-box formula-positive">
                            <span class="formula-box-label">Позитивный</span>
                            <span class="formula-box-value">
                                {{ player.reception_positive_only || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.reception_positive_only || 0, player.reception_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-poor">
                            <span class="formula-box-label">/</span>
                            <span class="formula-box-value">
                                {{ player.reception_poor || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.reception_poor || 0, player.reception_total) }}%)
                                </span>
                            </span>
                        </span>
                        <span class="formula-text"> - </span>
                        <span class="formula-box formula-errors">
                            <span class="formula-box-label">Ошибки</span>
                            <span class="formula-box-value">
                                {{ player.reception_neutral || 0 }}
                                <span class="formula-percentage">
                                    ({{ getPercentage(player.reception_neutral || 0, player.reception_total) }}%)
                                </span>
                            </span>
                        </span>
                    </div>
                    <div class="formula-fraction-line"></div>
                    <div class="formula-denominator">
                        <span class="formula-box formula-total">
                            <span class="formula-box-label">Всего приемов</span>
                            <span class="formula-box-value">{{ player.reception_total }}</span>
                        </span>
                    </div>
                </div>
                <span class="formula-text"> = </span>
                <span class="formula-box formula-result">
                    <span class="formula-box-label">Эффективность</span>
                    <span class="formula-box-value">{{ formatEfficiency(player.reception_efficiency) }}%</span>
                </span>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    player: {
        type: Object,
        required: true
    },
    category: {
        type: String,
        required: true,
        validator: (value) => ['attack', 'serve', 'block', 'reception'].includes(value)
    },
    theme: {
        type: String,
        default: 'light',
        validator: (value) => ['light', 'dark'].includes(value)
    }
});

const themeClass = computed(() => {
    return `theme-${props.theme}`;
});

const formatEfficiency = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value).toFixed(2);
};

const getPercentage = (value, total) => {
    if (!total || total === 0) return '0';
    return Number((value / total) * 100).toFixed(1);
};
</script>

<style lang="scss" scoped>
// Цвета
$color-green: #77c144;
$color-red: rgb(244, 67, 54);
$color-gray: hsla(0, 1%, 56%, 0.6);
$color-blue: rgba(33, 150, 243, 0.6);

// Общие стили для обоих тем (топ1 и топ2-5 одинаковые)
.stat-item.formula-item {
    width: 100%;
    padding: 0.75rem;
    border-radius: 5px;
    margin-top: 0.5rem;
    justify-content: center;
    width: 100%;
    display: flex;
    height: 5.5rem;
    align-items: center;

    // Стили контейнера одинаковые для обеих тем
    &.theme-light,
    &.theme-dark {
        background: #fff;
        border: 1px solid #e0e0e0;
    }

    .efficiency-formula {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.2rem;
        font-size: 0.55rem;
        line-height: 1.4;

        .formula-text {
            white-space: nowrap;
            color: #1a1a1a;
        }

        .formula-box {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-width: 2rem;
            padding: 0.25rem 0.4rem;
            border-radius: 4px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
            gap: 0.1rem;
            border: 1px solid rgba(0, 0, 0, 0.1);

            .formula-box-label {
                font-size: 0.5rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                white-space: nowrap;
                line-height: 1;
                color: #ffffff;
            }

            .formula-box-value {
                font-size: 0.75rem;
                font-weight: 700;
                color: #fff;
                line-height: 1;

                .formula-percentage {
                    font-size: 0.6rem;
                    font-weight: 400;
                }
            }

            // Зеленый - для очков (kills, aces)
            &.formula-kills,
            &.formula-aces {
                background: $color-green;
            }

            // Красный - для ошибок
            &.formula-errors,
            &.formula-negative {
                background: $color-red;
            }

            // Серый - для нейтральных значений
            &.formula-blocked,
            &.formula-positive,
            &.formula-insufficient,
            &.formula-very-positive,
            &.formula-neutral,
            &.formula-poor,
            &.formula-soft,
            &.formula-remain {
                background: $color-gray;
            }

            // Подсказки для некоторых элементов
            &.formula-positive,
            &.formula-insufficient,
            &.formula-very-positive,
            &.formula-remain {
                cursor: help;
            }

            // Серый - для знаменателя (total)
            &.formula-total {
                background: $color-gray;
            }

            // Синий - для результата (result)
            &.formula-result {
                background: $color-blue;
                font-size: 0.65rem;

                .formula-box-value {
                    color: #fff;
                }
            }
        }

        .formula-fraction {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin: 0 0.2rem;

            .formula-numerator {
                display: flex;
                align-items: center;
                gap: 0.2rem;
                flex-wrap: wrap;
                justify-content: center;
            }

            .formula-fraction-line {
                width: 100%;
                height: 1px;
                margin: 0.2rem 0;
                min-width: 120px;
                background: rgba(0, 0, 0, 0.2);
            }

            .formula-denominator {
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }
}
</style>
